import React, { useState, useContext, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { NotificationContext } from '../contexts/NotificationContext';
import { getValidAfid } from '../utils/afidHandler';
import ParticleBackground from '../components/layout/ParticleBackground';
import Navigation from '../components/layout/Navigation';
import Footer from '../components/layout/Footer';

interface LoginProps {
  setIsLoggedIn: React.Dispatch<React.SetStateAction<boolean>>;
}

// Animations matching the website design
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideDownFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideUpFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Main container with transparent background for ParticleBackground
const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: transparent;
  color: var(--color-text);
  position: relative;
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 6rem auto 4rem;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    margin: 4rem auto 2rem;
    padding: 1rem;
    justify-content: flex-start;
    min-height: calc(100vh - 6rem);
  }
`;

// Glass-morphism card matching the website design
const LoginCard = styled.div`
  background: var(--color-card-bg);
  backdrop-filter: blur(20px);
  border: 1px solid var(--color-border);
  border-radius: 20px;
  padding: 3rem 2.5rem;
  width: 100%;
  max-width: 450px;
  position: relative;
  z-index: 1;
  animation: ${fadeIn} 0.8s ease-out forwards;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    margin: 1rem;
    max-width: calc(100vw - 2rem);
  }
`;

const Title = styled.h1`
  color: var(--color-text);
  font-size: 2.5rem;
  margin-bottom: 2rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  position: relative;
  animation: ${slideDownFadeIn} 0.6s ease-out forwards;
  opacity: 0;
  font-family: 'Maragsa-Display', Helvetica, Arial, sans-serif;
  text-align: center;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 2px;
    background: linear-gradient(90deg, transparent, var(--color-primary), transparent);
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const Input = styled.input`
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  background-color: rgba(255, 255, 255, 0.05);
  color: var(--color-text);
  width: calc(100% - 3rem);
  font-size: 1rem;
  font-family: 'WorkSans', Helvetica, Arial, sans-serif;
  transition: all 0.3s ease;
  animation: ${slideUpFadeIn} 0.6s ease-out 0.3s forwards;
  opacity: 0;
  
  &::placeholder {
    color: var(--color-text-secondary);
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
    background-color: rgba(255, 255, 255, 0.08);
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.1);
  }
`;

interface ButtonProps {
  $theme: 'light' | 'dark';
}

const Button = styled.button<ButtonProps>`
  background: transparent;
  color: var(--color-primary);
  border: 2px solid var(--color-primary);
  padding: 1rem 2.5rem;
  font-size: 1.1rem;
  font-weight: 500;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  animation: ${slideUpFadeIn} 0.6s ease-out 0.6s forwards;
  opacity: 0;
  position: relative;
  overflow: hidden;
  font-family: 'WorkSans', Helvetica, Arial, sans-serif;
  width: 100%;
  margin-top: 0.5rem;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }
  
  &:hover {
    background: var(--color-primary);
    color: ${props => props.$theme === 'dark' ? '#000000' : '#ffffff'};
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(255, 255, 255, 0.2);
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    
    &:hover {
      background: transparent;
      color: var(--color-primary);
      box-shadow: none;
    }
  }
`;

const ResendButton = styled(Button)`
  background: transparent;
  border-color: var(--color-secondary);
  color: var(--color-secondary);
  
  &:hover {
    background: var(--color-secondary);
    color: ${props => props.$theme === 'dark' ? '#000000' : '#ffffff'};
  }
`;

const LinkText = styled.p`
  text-align: center;
  margin-top: 1.5rem;
  font-size: 0.95rem;
  color: var(--color-text);
  animation: ${fadeIn} 0.6s ease-out 0.9s forwards;
  opacity: 0;
  font-family: 'WorkSans', Helvetica, Arial, sans-serif;

  a {
    color: var(--color-primary);
    text-decoration: none;
    font-weight: 500;
    transition: all 0.3s ease;

    &:hover {
      color: var(--color-secondary);
      text-decoration: underline;
      text-underline-offset: 3px;
    }
  }
`;

const SeparatorLine = styled.hr`
  width: 100%;
  border: 0;
  border-top: 1px solid var(--color-border);
  margin: 2rem 0;
  animation: ${fadeIn} 0.6s ease-out 1.2s forwards;
  opacity: 0;
`;

const GoogleButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  animation: ${fadeIn} 0.6s ease-out 1.5s forwards;
  opacity: 0;

  #googleSignInDiv {
    width: 100%;
    max-width: 300px;
    
    iframe {
      border-radius: 12px !important;
    }
  }
`;

const Login: React.FC<LoginProps> = ({ setIsLoggedIn }) => {
  const navigate = useNavigate();
  const { showNotification } = useContext(NotificationContext);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showResend, setShowResend] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleGoogleLogin = useCallback(async (response: any) => {
    try {
      const googleToken = response.credential;
      const afid = getValidAfid();

      const res = await axios.post('https://riskmaster.pro/google_login_proxy.php', {
        token: googleToken,
        afid: afid,
      });

      const resData = res.data;
      if (resData.status === 'success') {
        localStorage.setItem('token', resData.token);
        setIsLoggedIn(true);

        if (resData.new_user) {
          showNotification('Signed up successfully with Google.');
        } else {
          showNotification('Logged in successfully with Google.');
        }

        navigate('/');
      } else {
        showNotification(resData.message || 'Google login failed.');
      }
    } catch (error) {
      showNotification('An error occurred during Google login. Please try again.');
      console.error('Google login error:', error);
    }
  }, [setIsLoggedIn, showNotification, navigate]);

  useEffect(() => {
    if (window.fbq) {
      window.fbq('track', 'ViewContent', { page: 'Login' });
    }
    if (window.google) {
      window.google.accounts.id.initialize({
        client_id: '145472120026-mavhgm62df5ud8a1p33vcif1klbts72f.apps.googleusercontent.com',
        callback: handleGoogleLogin,
      });

      window.google.accounts.id.renderButton(
        document.getElementById('googleSignInDiv'),
        { theme: 'outline', size: 'large' }
      );
    } else {
      console.error('Google API not loaded');
    }
  }, [handleGoogleLogin]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);

    if (window.fbq) {
      window.fbq('track', 'LoginAttempt');
    }

    try {
      const afid = getValidAfid();
      const formData = new FormData();
      formData.append('email', email);
      formData.append('password', password);
      if (afid) {
        formData.append('afid', afid);
      }

      const response = await axios.post(
        'https://riskmaster.pro/login_proxy.php',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const resData = response.data;
      if (resData.status === 'success') {
        localStorage.setItem('token', resData.token);
        setIsLoggedIn(true);
        showNotification('Logged in successfully.');
        navigate('/');
      } else {
        showNotification(resData.message || 'Login failed.');
        if (resData.message && resData.message.includes('not confirmed')) {
          setShowResend(true);
        }
      }
    } catch (error) {
      showNotification('An error occurred during login. Please try again.');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (window.fbq) {
      window.fbq('track', 'ResendConfirmation');
    }

    try {
      const formData = new FormData();
      formData.append('email', email);

      const response = await axios.post(
        'https://riskmaster.pro/resend_confirmation_proxy.php',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const resData = response.data;
      if (resData.status === 'success') {
        showNotification('Confirmation email resent. Please check your inbox.');
      } else {
        showNotification(resData.message || 'Failed to resend confirmation email.');
      }
    } catch (error) {
      showNotification('An error occurred while resending the confirmation email. Please try again.');
      console.error(error);
    }
  };

  return (
    <LoginContainer>
      <Helmet>
        <title>Login to RiskMaster | RiskMaster</title>
        <meta
          name="description"
          content="Login to your RiskMaster account to access advanced risk management tools for your trading strategy."
        />
        <link rel="canonical" href="https://www.riskmaster.net/login" />
      </Helmet>
      <ParticleBackground />
      <Navigation
        activeSection="login"
        handleScroll={() => {}}
        isLoggedIn={false}
        onLogout={() => {}}
      />
      <ContentWrapper>
        <LoginCard>
          <Title>Login</Title>
          <Form onSubmit={handleSubmit}>
            <Input
              type="email"
              placeholder="Email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Input
              type="password"
              placeholder="Password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button type="submit" $theme="dark" disabled={isLoading}>
              {isLoading ? 'Logging In...' : 'Log In'}
            </Button>
            {showResend && (
              <ResendButton type="button" onClick={handleResendConfirmation} $theme="dark">
                Resend Confirmation Email
              </ResendButton>
            )}
          </Form>
          <LinkText>
            Don't have an account? <Link to="/signup">Sign Up</Link>
          </LinkText>
          <LinkText>
            Forgot your password? <Link to="/forgot-password">Reset it here</Link>
          </LinkText>
          <SeparatorLine />
          <GoogleButtonWrapper>
            <div id="googleSignInDiv"></div>
          </GoogleButtonWrapper>
        </LoginCard>
      </ContentWrapper>
      <Footer />
    </LoginContainer>
  );
};

export default Login;
